import React, { useMemo } from 'react';
import styled from 'styled-components';

import { useRouterLocation } from '#/components/hooks';
import DropdownMenuItem from '#/components/layout/header/HeaderMenuDropdownItem';
import {
  DividerHorizontalItem,
  HorizontalSpacerItem,
} from '#/components/layout/header/items/DividerItems';
import LockWalletItem from '#/components/layout/header/items/LockWalletItem';
import { getWalletIcon } from '#/components/wallet/icons/walletIcon';
import { MAINTENANCE_MODE } from '#/config/constants';
import { useIsRestrictedRegion } from '#/hooks/useIsRestrictedRegion';
import { useDiscoverEip6961WalletByRdns } from '#/lib/ethereum/walletDiscovery/walletDiscovery';
import { textTrimWith3DotsInMiddle } from '#/lib/string';
import { staticDispatch, useSelector } from '#/reduxState/store';
import { showWalletModal } from '#/reduxState/wallet/walletDetailsReducer';

import profileIcon from '../images/profile.svg';

import userEmailItem from './UserEmailItem';
import vaultOwnerItem from './VaultOwnerItem';
import walletBlockExplorerItem from './WalletBlockExplorerItem';
import walletCopyAddressItem from './WalletCopyAddressItem';

import type {
  CommonItemProps,
  MenuItemType,
} from '#/components/layout/header/headerTypes';

const LabelWrapper = styled.div`
  position: relative;
  font-size: 28px;
  padding-left: 8px;
  padding-right: 8px;
  display: flex;
  align-items: center;
  font-size: var(--medium);
  color: var(--white);
  height: var(--header-height);
`;

function WalletDropdownComponent({ $menuItem }: CommonItemProps) {
  const { pathname } = useRouterLocation();
  const loggedIn = useSelector(state => state.currentUser.loggedIn);
  const isRestrictedRegion = useIsRestrictedRegion();
  const isCatchAllUser = useSelector(state => state.currentUser.isCatchAllUser);
  const walletAddress = useSelector(
    state => state.wallet.walletAddressOriginal
  );
  const walletDiscoveryRdns = useSelector(
    state => state.wallet.walletDiscoveryRdns
  );
  const walletType = useSelector(state => state.wallet.walletType);
  const isLoggedIn = loggedIn && !isCatchAllUser;
  const unlockedOrLogggedIn = !!walletAddress || isLoggedIn;

  const activeDiscoveredWallet = useDiscoverEip6961WalletByRdns(
    walletDiscoveryRdns,
    true
  );
  const customWalletIcon = activeDiscoveredWallet?.info.icon;

  const addressShortened = useMemo(
    // TODO: reuse truncate from string utils ? (just more chars)
    () => '0x' + textTrimWith3DotsInMiddle(walletAddress.replace('0x', ''), 8),
    [walletAddress]
  );

  if ($menuItem.type !== 'MenuDropdown') {
    console.error('Menu setup problem');
    return null;
  }

  const walletIcon = walletType
    ? getWalletIcon(walletType, customWalletIcon)
    : undefined;

  const loggedInWithoutWallet = !walletType && isLoggedIn;

  if (!walletAddress && MAINTENANCE_MODE) {
    return null;
  }

  return (
    <DropdownMenuItem
      disableDropdown={isRestrictedRegion || !unlockedOrLogggedIn}
      leftAligned={!loggedInWithoutWallet}
      onClick={() => {
        staticDispatch(showWalletModal({ afterGoTo: pathname }));
      }}
      // paddingRight={'0'}
      paddingRight={unlockedOrLogggedIn ? '0' : '8px'}
      hoverColor={unlockedOrLogggedIn ? undefined : 'var(--primary7)'}
      menuItem={{
        ...$menuItem,
        label: (
          <LabelWrapper>
            {walletIcon && (
              <img
                alt="wallet"
                style={{ maxHeight: 16, width: 16 }}
                src={walletIcon}
              />
            )}
            {loggedInWithoutWallet && <img src={profileIcon} />}
            {unlockedOrLogggedIn ? (
              <>{walletAddress && <span style={{ width: 9 }} />}</>
            ) : (
              'Connect Wallet'
            )}
            {walletAddress && `${addressShortened}`}
          </LabelWrapper>
        ),
      }}
    />
  );
}

const WalletMenuDropdownItem: MenuItemType = {
  type: 'MenuDropdown' as const,
  Component: WalletDropdownComponent,
  // Align with Dropdown label
  itemsMinWidth: '181px',
  items: [
    HorizontalSpacerItem,
    walletCopyAddressItem,
    walletBlockExplorerItem,
    vaultOwnerItem,
    // API-Users can be logged in
    userEmailItem,
    DividerHorizontalItem,
    LockWalletItem,
    HorizontalSpacerItem,
  ],
};

export default WalletMenuDropdownItem;
